  import { Injectable } from '@angular/core';
  import { Store } from '@ngrx/store';
  import { Actions, createEffect, ofType } from '@ngrx/effects';
  import { of } from 'rxjs';
  import {
    catchError,
    filter,
    map,
    mergeMap,
    tap,
    withLatestFrom,
  } from 'rxjs/operators';
  import {
    RESELLER_FIND,
    RESELLER_FIND_ALL,
    RESELLER_FIND_SUCCESS,
    RESELLER_FIND_ALL_SUCCESS,
    RESELLER_UPDATE_LOADING,
    RESELLER_EDIT_MODE,
    RESELLER_FIND_NAME,
    RESELLER_UPDATE_ID,
    SERVICE_FAILURE,
    RESELLER_UPDATE_VIEW,
    RESELLER_CREATE_VIEW,
    RESELLER_CREATE,
    NOTIFICATION_SHOW,
    RESELLER_CREATE_COMPLETED,
    RESELLER_RESET_ERROR,
    RESELLER_UPDATE,
    RESELLER_UPDATE_COMPLETED,
    RESELLER_REMOVE,
  } from '@store/actions';
  import { Revendedor, RevendedorForm } from '@core/models';
  import { ResellerService } from '@core/services';
  import { selectAuthenticationIsSuperUser, selectReseller, selectResellerForm } from '@store/selectors';

  @Injectable()
  export class ResellerEffects {
    constructor(
      private actions$: Actions,
      private store: Store,
      private resellerService: ResellerService
    ) {}

    createForm$ = createEffect(() =>
      this.actions$.pipe(
        ofType(RESELLER_CREATE_VIEW),
        mergeMap(() => {
          return [RESELLER_EDIT_MODE({ editMode: true, isEditing: false })];
        })
      )
    );

    editForm$ = createEffect(() =>
      this.actions$.pipe(
        ofType(RESELLER_UPDATE_VIEW),
        mergeMap(({ resellerId }) => {
          return [
            RESELLER_UPDATE_ID({ resellerId }),
            RESELLER_FIND({ id: resellerId }),
            RESELLER_EDIT_MODE({ editMode: true, isEditing: true }),
          ];
        })
      )
    );

    find$ = createEffect(() =>
      this.actions$.pipe(
        ofType(RESELLER_FIND),
        filter((action) => action.id !== null),
        mergeMap(({ id }) => {
          this.store.dispatch(RESELLER_UPDATE_LOADING({ loading: true }));
          return this.resellerService.getReseller(id).pipe(
            mergeMap(({ data }) => {
              const [reseller] = data as Revendedor[];

              return [
                RESELLER_UPDATE_LOADING({ loading: false }),
                RESELLER_FIND_SUCCESS({ reseller }),
              ];
            }),
            catchError((error) => 
              of(error).pipe(
                mergeMap((error) => {
                  return [
                    SERVICE_FAILURE({ error }),
                    RESELLER_UPDATE_LOADING({ loading: false }),
                  ];
                })
              )
            )
          );
        })
      )
    );

    findByName$ = createEffect(() =>
      this.actions$.pipe(
        ofType(RESELLER_FIND_NAME),
        filter((action) => action.nombre !== null),
        mergeMap(({ nombre }) => {
          this.store.dispatch(RESELLER_UPDATE_LOADING({ loading: true }));
          return this.resellerService.getNameResellers(nombre).pipe(
            mergeMap(({ data }) => {
              const resellerList = data as Revendedor[];

              return [
                RESELLER_FIND_ALL_SUCCESS({ resellerList }),
                RESELLER_UPDATE_LOADING({ loading: false }),
              ];
            }),
            catchError((error) => 
              of(error).pipe(
                mergeMap((error) => {
                  return [
                    SERVICE_FAILURE({ error }),
                    RESELLER_UPDATE_LOADING({ loading: false }),
                  ];
                })
              )
            )
          );
        })
      )
    );

    findAll$ = createEffect(() =>
      this.actions$.pipe(
        ofType(RESELLER_FIND_ALL),
        withLatestFrom(this.store.select(selectAuthenticationIsSuperUser)),
        // filter(([_, isSuperUser]) => !!isSuperUser),
        mergeMap(() => {
          this.store.dispatch(RESELLER_UPDATE_LOADING({ loading: true }));

          return this.resellerService.getResellers().pipe(
            mergeMap(({ data }) => {
              const resellerList = data as Revendedor[];

              return [
                RESELLER_FIND_ALL_SUCCESS({ resellerList }),
                RESELLER_UPDATE_LOADING({ loading: false }),
              ];
            }),
            catchError((error) => 
              of(error).pipe(
                mergeMap((error) => {
                  return [
                    SERVICE_FAILURE({ error }),
                    RESELLER_UPDATE_LOADING({ loading: false }),
                  ];
                })
              )
            )
          );
        }),
      )
    );

    create$ = createEffect(() =>
      this.actions$.pipe(
        ofType(RESELLER_CREATE),
        withLatestFrom(this.store.select(selectResellerForm)),
        filter(([_, resellerForm]) => !!resellerForm),
        map(([_, resellerForm]) => {
          return { resellerForm: resellerForm as RevendedorForm };
        }),
        mergeMap(({ resellerForm }) => {
          this.store.dispatch(RESELLER_UPDATE_LOADING({ loading: true }));
          return this.resellerService.create(resellerForm).pipe(
            mergeMap(() => {
              return [
                NOTIFICATION_SHOW({ notification: { type: 'success', title: 'Success', detail: 'Reseller successfully created'} }),
                RESELLER_UPDATE_LOADING({ loading: false }),
                RESELLER_EDIT_MODE({ editMode: false, isEditing: false }),
                RESELLER_CREATE_COMPLETED(),
              ];
            }),
            catchError((error) => {
              return of(error).pipe(
                mergeMap((error) => {
                  return [
                    SERVICE_FAILURE({ error }),
                    RESELLER_RESET_ERROR(),
                    RESELLER_UPDATE_LOADING({ loading: false }),
                  ];
                })
              );
            })
          );
        })
      )
    );

    creationComplete$ = createEffect(() =>
      this.actions$.pipe(
        ofType(RESELLER_CREATE_COMPLETED),
        tap(() => {
          this.resellerService.creationCompleted();
        })
      ),
      { dispatch: false }
    )

    update$ = createEffect(() =>
      this.actions$.pipe(
        ofType(RESELLER_UPDATE),
        withLatestFrom(
          this.store.select(selectResellerForm),
          this.store.select(selectReseller)
        ),
        filter(([_, resellerId, resellerForm]) => !!resellerId && !!resellerForm),
        map(([_, resellerForm, resellerId]) => {
          return {
            id: resellerId?.id as string,
            resellerForm: resellerForm as RevendedorForm,
          };
        }),
        mergeMap(({ id, resellerForm }) => {
          this.store.dispatch(RESELLER_UPDATE_LOADING({ loading: true }));
          return this.resellerService.update(id, resellerForm).pipe(
            mergeMap(() => {
              return [
                RESELLER_UPDATE_LOADING({ loading: false }),
                NOTIFICATION_SHOW({ notification: { type: 'success', title: 'Success', detail: 'Reseller successfully updated' } }),
                RESELLER_EDIT_MODE({ editMode: false, isEditing: false }),
                RESELLER_UPDATE_COMPLETED(),
              ];
            }),
            catchError((error) => {
              return of(error).pipe(
                mergeMap((error) => {
                  return [
                    SERVICE_FAILURE({ error }),
                    RESELLER_RESET_ERROR(),
                    RESELLER_UPDATE_LOADING({ loading: false }),
                  ];
                })
              );
            })
          );
        })
      )
    );

    updateComplete$ = createEffect(() =>
      this.actions$.pipe(
        ofType(RESELLER_UPDATE_COMPLETED),
        tap(() => {
          this.resellerService.updateCompleted();
        })
      ),
      { dispatch: false }
    )

    remove$ = createEffect(() =>
      this.actions$.pipe(
        ofType(RESELLER_REMOVE),
        filter((action) => action.id !== null),
        mergeMap(({ id }) => {
          this.store.dispatch(RESELLER_UPDATE_LOADING({ loading: true }));
          return this.resellerService.removeReseller(id).pipe(
            mergeMap(() => {
              return [
                NOTIFICATION_SHOW({ notification: { type: 'success', title: 'Success', detail: 'Reseller removed successfully' } }),
                RESELLER_UPDATE_LOADING({ loading: false }),
                RESELLER_FIND_ALL(),
              ];
            }),
            catchError((error) => {
              return of(error).pipe(
                mergeMap((error) => {
                  return [SERVICE_FAILURE({ error }), RESELLER_RESET_ERROR()];
                })
              );
            })
          );
        })
      )
    );
  }
